/**
 * Odds conversion and calculation utilities
 */

/**
 * Find the greatest common divisor of two numbers
 */
function gcd(a: number, b: number): number {
  a = Math.abs(Math.round(a))
  b = Math.abs(Math.round(b))
  while (b) {
    const t = b
    b = a % b
    a = t
  }
  return a
}

/**
 * Convert decimal odds to fractional (e.g., 2.5 -> "3/2")
 */
export function decimalToFractional(decimal: number): string {
  if (decimal <= 1) return '0/1'

  // Convert to fractional: decimal odds = fractional + 1
  // e.g., 2.5 = 3/2 + 1, so numerator/denominator = 1.5 = 3/2
  const profit = decimal - 1

  // Handle common fractional odds precisely
  const commonOdds: Record<number, string> = {
    0.1: '1/10',
    0.2: '1/5',
    0.25: '1/4',
    0.33: '1/3',
    0.4: '2/5',
    0.5: '1/2',
    0.57: '4/7',
    0.67: '2/3',
    0.8: '4/5',
    0.91: '10/11',
    1: '1/1',
    1.1: '11/10',
    1.2: '6/5',
    1.33: '4/3',
    1.5: '3/2',
    1.57: '11/7',
    1.67: '5/3',
    1.8: '9/5',
    2: '2/1',
    2.25: '9/4',
    2.5: '5/2',
    2.75: '11/4',
    3: '3/1',
    3.5: '7/2',
    4: '4/1',
    4.5: '9/2',
    5: '5/1',
    6: '6/1',
    7: '7/1',
    8: '8/1',
    9: '9/1',
    10: '10/1',
  }

  // Check for close match in common odds
  for (const [key, value] of Object.entries(commonOdds)) {
    if (Math.abs(profit - parseFloat(key)) < 0.02) {
      return value
    }
  }

  // For other values, calculate the fraction
  // Multiply by 100 to work with integers
  const multiplier = 100
  let numerator = Math.round(profit * multiplier)
  let denominator = multiplier

  const divisor = gcd(numerator, denominator)
  numerator = numerator / divisor
  denominator = denominator / divisor

  return `${numerator}/${denominator}`
}

/**
 * Convert fractional odds to decimal (e.g., "3/2" -> 2.5)
 */
export function fractionalToDecimal(fractional: string): number {
  const parts = fractional.split('/')
  if (parts.length !== 2) return 0

  const numerator = parseFloat(parts[0])
  const denominator = parseFloat(parts[1])

  if (denominator === 0) return 0

  return (numerator / denominator) + 1
}

/**
 * Convert probability percentage to decimal odds (e.g., 40% -> 2.5)
 */
export function probabilityToDecimalOdds(pct: number): number {
  if (pct <= 0) return Infinity
  if (pct >= 100) return 1

  return 100 / pct
}

/**
 * Convert probability percentage to fractional odds
 */
export function probabilityToFractionalOdds(pct: number): string {
  const decimal = probabilityToDecimalOdds(pct)
  return decimalToFractional(decimal)
}

/**
 * Convert decimal odds to implied probability percentage
 */
export function decimalOddsToImpliedProbability(odds: number): number {
  if (odds <= 0) return 0
  return (1 / odds) * 100
}

/**
 * Calculate potential return including stake
 */
export function calculateReturn(stake: number, odds: number): number {
  return stake * odds
}

/**
 * Calculate profit (return minus stake)
 */
export function calculateProfit(stake: number, odds: number): number {
  return stake * (odds - 1)
}

/**
 * Calculate value percentage (our probability vs implied probability from odds)
 * Positive = value bet, Negative = odds are worse than our probability suggests
 */
export function calculateValue(ourProbability: number, decimalOdds: number): number {
  const impliedProb = decimalOddsToImpliedProbability(decimalOdds)
  return ourProbability - impliedProb
}

/**
 * Result of a value calculation for display purposes
 */
export type ValueDisplayResult = {
  edge: number // Positive = value, negative = bad value
  edgeLabel: string // Formatted string like "+11.4%" or "-3.2%"
  impliedProbability: number // What the odds imply
  isValue: boolean // edge >= threshold
}

/**
 * Calculate value and return display-ready result
 * @param ourProbability - Our computed probability (0-100)
 * @param decimalOdds - The bookmaker's decimal odds
 * @param minThreshold - Minimum edge to consider "value" (default 0)
 */
export function calculateValueDisplay(
  ourProbability: number,
  decimalOdds: number | null,
  minThreshold: number = 0
): ValueDisplayResult | null {
  if (decimalOdds == null || decimalOdds <= 1) return null

  const impliedProb = decimalOddsToImpliedProbability(decimalOdds)
  const edge = ourProbability - impliedProb
  const sign = edge >= 0 ? '+' : ''

  return {
    edge,
    edgeLabel: `${sign}${edge.toFixed(1)}%`,
    impliedProbability: impliedProb,
    isValue: edge >= minThreshold && edge > 0,
  }
}

/**
 * Get CSS class for edge color based on value
 * @param edge - The edge percentage (positive = value)
 */
export function getEdgeColorClass(edge: number | null): string {
  if (edge == null) return ''
  if (edge > 0) return 'edge-positive'
  if (edge < -2) return 'edge-negative'
  return 'edge-neutral'
}

/**
 * Format decimal odds to 2 decimal places
 */
export function formatDecimalOdds(odds: number): string {
  return odds.toFixed(2)
}

/**
 * Format currency amount
 */
export function formatCurrency(amount: number, symbol: string = '£'): string {
  return `${symbol}${amount.toFixed(2)}`
}
